import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  cancelWmsOrderRequest,
  cancelWmsOrdersBulkRequest,
  getWmsOrderRequest,
  getWmsOrdersRequest,
  reserveWmsOrderRequest,
  reserveWmsOrdersBulkRequest,
} from './wmsOrdersApi';
import { ALL_FILTER } from './wmsOrdersUtils';
import { selectCurrentWarehouseId } from '../warehouseContext/warehouseContextSlice';
import { extractApiError } from '../../utils/errors';

/**
 * Loads the page of orders the filters describe, in the warehouse in use — or
 * in every warehouse in reach when none is picked.
 *
 * @returns {Promise<{orders: Array<object>, count: number, state_counts: object,
 *   window_from: string|null, elsewhere: object|null}>}
 */
export const fetchWmsOrders = createAsyncThunk(
  'wmsOrders/fetchWmsOrders',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const { status, company, source, search, dateFrom, dateTo, page, rowsPerPage } =
        state.wmsOrders.filters;

      const params = { page, page_size: rowsPerPage };
      const warehouseId = selectCurrentWarehouseId(state);
      if (warehouseId) params.warehouse = warehouseId;
      if (status !== ALL_FILTER) params.state = status;
      if (company !== ALL_FILTER) params.company = company;
      if (source !== ALL_FILTER) params.source = source;
      if (search) params.search = search;
      // Sent only when chosen: without it the server opens its own window of
      // thirty days and says so in `window_from`.
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await getWmsOrdersRequest(params);
      return response.data;
    } catch (error) {
      console.error('Error fetching warehouse orders:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron obtener las órdenes'));
    }
  }
);

/**
 * One order with everything its state was deduced from.
 *
 * @param {number|string} orderId
 */
export const fetchWmsOrder = createAsyncThunk(
  'wmsOrders/fetchWmsOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const response = await getWmsOrderRequest(orderId);
      return response.data;
    } catch (error) {
      console.error('Error fetching warehouse order:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo abrir la orden'));
    }
  }
);

/**
 * Cancels an order before it leaves, and reloads the listing.
 *
 * Resolves with what the server did: the row as it is now, how many units it
 * released, how many stay on the table, and whether the courier could be told.
 *
 * @param {number} orderId
 */
export const cancelWmsOrder = createAsyncThunk(
  'wmsOrders/cancelWmsOrder',
  async (orderId, { dispatch, rejectWithValue }) => {
    try {
      const response = await cancelWmsOrderRequest(orderId);
      dispatch(fetchWmsOrders());
      return response.data;
    } catch (error) {
      console.error('Error cancelling warehouse order:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo cancelar la orden'));
    }
  }
);

/**
 * Reserves an order from the listing or its page, without entering the
 * preparing screen. The listing is asked again, because the state changed.
 *
 * @param {number} orderId
 * @returns {Promise<object>} The row, with `reserved_now`, `lines_complete`, `lines_total`.
 */
export const reserveWmsOrder = createAsyncThunk(
  'wmsOrders/reserveWmsOrder',
  async (orderId, { dispatch, rejectWithValue }) => {
    try {
      const response = await reserveWmsOrderRequest(orderId);
      dispatch(fetchWmsOrders());
      return response.data;
    } catch (error) {
      console.error('Error reserving warehouse order:', error);
      return rejectWithValue(extractApiError(error, 'No se pudo reservar la orden'));
    }
  }
);

/**
 * Reserves the ticked orders in one go. The server answers order by order;
 * the listing is asked again because some of them changed state.
 *
 * @param {Array<number>} orderIds
 * @returns {Promise<{reserved: Array<object>, problems: Array<{id: number, message: string}>, units: number, message: string}>}
 */
export const reserveWmsOrdersBulk = createAsyncThunk(
  'wmsOrders/reserveWmsOrdersBulk',
  async (orderIds, { dispatch, rejectWithValue }) => {
    try {
      const response = await reserveWmsOrdersBulkRequest(orderIds);
      dispatch(fetchWmsOrders());
      return response.data;
    } catch (error) {
      console.error('Error reserving warehouse orders:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron reservar las órdenes'));
    }
  }
);

/**
 * Cancels the ticked orders in one go, each with the rules of a single cancel.
 *
 * @param {Array<number>} orderIds
 * @returns {Promise<{cancelled: Array<object>, problems: Array<{id: number, message: string}>, message: string}>}
 */
export const cancelWmsOrdersBulk = createAsyncThunk(
  'wmsOrders/cancelWmsOrdersBulk',
  async (orderIds, { dispatch, rejectWithValue }) => {
    try {
      const response = await cancelWmsOrdersBulkRequest(orderIds);
      dispatch(fetchWmsOrders());
      return response.data;
    } catch (error) {
      console.error('Error cancelling warehouse orders:', error);
      return rejectWithValue(extractApiError(error, 'No se pudieron cancelar las órdenes'));
    }
  }
);
